import {
    Box,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { Info, TriangleAlert } from 'lucide-react';
import { ReactNode, RefObject, useCallback, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import CopyField from '~/components/Fields/CopyField';
import DraggablePanel from '~/components/toolbar/shared/DraggablePanel';
import { STRUCTURE_SET_NOT_SHOWN_HINT_ID } from '~/hooks/target/structureSetNotShown';
import {
    AGREEMENT_COLOR,
    ComparisonRegion,
    rgb8ToCss,
} from '~/engine/core/render/renderers/util/comparisonColors';
import { ContourComparisonMetrics } from '~/domain/comparison/contourComparisonMetrics';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { useContourComparisonMetrics } from '~/hooks/viewer/useContourComparisonMetrics';
import {
    comparablePair,
    compareModeActiveState,
    comparisonRoiAState,
    comparisonRoiBState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { useComparisonEditToken } from '~/hooks/viewer/useComparisonEditToken';
import { comparisonMetricRows } from './comparisonMetricRows';
import { dicomColorToRgbString } from '~/utils/color';

const PANEL_WIDTH = 300;

interface RoiSelectProps {
    labelId: string;
    value: number | null;
    rois: ReadonlyArray<RoiReadModel>;
    onChange: (roiNumber: number | null) => void;
}

/** CSS string for an ROI's own DICOM colour. */
function roiCss(roi: RoiReadModel): string {
    return dicomColorToRgbString(roi.color);
}

/**
 * Small colour swatch — circular for ROI identity (matches the structure-list
 * dots), square for the overlay difference regions.
 */
function Swatch({
    color,
    circular = false,
    outline = false,
}: {
    color: string;
    circular?: boolean;
    outline?: boolean;
}) {
    return (
        <Box
            component="span"
            flexShrink={0}
            sx={{
                width: 10,
                height: 10,
                borderRadius: circular ? '50%' : '2px',
                ...(outline
                    ? { border: `2px solid ${color}`, bgcolor: 'transparent' }
                    : { bgcolor: color }),
            }}
        />
    );
}

/**
 * ROI colour dot + name, so a structure is identifiable by colour in the
 * picker list and the selected value — mirroring the structure list.
 */
function RoiOption({ roi }: { roi: RoiReadModel }) {
    return (
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <Swatch color={roiCss(roi)} circular />
            <Typography component="span" noWrap fontSize={13}>
                {roi.name}
            </Typography>
        </Box>
    );
}

function RoiSelect({ labelId, value, rois, onChange }: RoiSelectProps) {
    const intl = useIntl();
    const label = intl.formatMessage({ id: labelId });
    // DOM id wiring InputLabel → Select for screen readers. Derived from the
    // (unique) i18n key so the two pickers get distinct aria-labelledby targets.
    const selectLabelId = `${labelId}-label`;

    const handleChange = (event: SelectChangeEvent) => {
        const next = event.target.value;
        onChange(next === '' ? null : Number(next));
    };

    return (
        <FormControl size="small" fullWidth>
            <InputLabel id={selectLabelId}>{label}</InputLabel>
            <Select
                labelId={selectLabelId}
                label={label}
                value={value === null ? '' : String(value)}
                onChange={handleChange}
                renderValue={(selected) => {
                    const roi = rois.find(
                        (r) => String(r.roiNumber) === selected
                    );
                    return roi ? <RoiOption roi={roi} /> : null;
                }}
            >
                {rois.map((roi) => (
                    <MenuItem key={roi.roiNumber} value={String(roi.roiNumber)}>
                        <RoiOption roi={roi} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

/**
 * Two dropdowns selecting the ROI pair to compare (point ROIs excluded). Each
 * list also hides the other dropdown's current pick, so the same ROI can't be
 * chosen for both A and B (which `comparablePair` rejects as not comparable).
 * A mirrors the ROI under review (`selectedROIState`): it is set to the
 * current selection when compare opens and follows it as the clinician
 * switches ROIs while the panel stays open. On open the pair also starts fresh
 * — B is cleared so a stale pair never auto-runs.
 */
function RoiPicker() {
    const structureSet = useRecoilValue(currentStructureSetState);
    const [roiA, setRoiA] = useRecoilState(comparisonRoiAState);
    const [roiB, setRoiB] = useRecoilState(comparisonRoiBState);
    const selectedRoi = useRecoilValue(selectedROIState);

    const selectableRois = (structureSet?.roiArray ?? []).filter(
        (roi) => !roi.isPointRoi
    );

    // Re-armed on every open (the picker remounts each time compare opens).
    const isFreshOpen = useRef(true);

    // A mirrors the ROI under review. The first run after opening starts the
    // pair fresh — A = the current selection when it's a comparable non-point
    // ROI (otherwise empty), and B cleared so a stale pair never auto-runs.
    // Later runs (the clinician switching ROIs while the panel stays open) keep
    // A on the reviewed ROI, leaving it as-is when the selection isn't
    // comparable or is already B's pick (which would duplicate).
    useEffect(() => {
        const isComparable = selectableRois.some(
            (r) => r.roiNumber === selectedRoi
        );
        if (isFreshOpen.current) {
            isFreshOpen.current = false;
            setRoiA(isComparable ? selectedRoi : null);
            setRoiB(null);
            return;
        }
        if (isComparable && selectedRoi !== roiB) {
            setRoiA(selectedRoi);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRoi]);

    return (
        <Stack spacing={1.5}>
            <RoiSelect
                labelId="compare.roiA"
                value={roiA}
                rois={selectableRois.filter((r) => r.roiNumber !== roiB)}
                onChange={setRoiA}
            />
            <RoiSelect
                labelId="compare.roiB"
                value={roiB}
                rois={selectableRois.filter((r) => r.roiNumber !== roiA)}
                onChange={setRoiB}
            />
        </Stack>
    );
}

/**
 * Compact inline legend — the three regions read as one wrapping row of
 * swatch+label chips (`◼ Both  ◻ A  ◻ B`) to stay short on vertical space. The
 * label is just "Both" / each ROI name; the precise "only in …" meaning lives
 * in each chip's tooltip so the disagreement regions stay unambiguous on hover.
 * The agreement swatch is the fixed green fill; each ROI's swatch is an outline
 * ring in its own colour, mirroring how the overlay draws that ROI's contour on
 * the slice.
 */
function Legend({
    roiAName,
    roiBName,
    roiAColor,
    roiBColor,
}: {
    roiAName: string;
    roiBName: string;
    roiAColor: string;
    roiBColor: string;
}) {
    const intl = useIntl();
    const chips: {
        region: ComparisonRegion;
        label: string;
        tooltip: string;
        color: string;
        outline: boolean;
    }[] = [
        {
            region: ComparisonRegion.Agreement,
            label: intl.formatMessage({ id: 'compare.legend.agreement.short' }),
            tooltip: intl.formatMessage({ id: 'compare.legend.agreement' }),
            color: rgb8ToCss(AGREEMENT_COLOR),
            outline: false,
        },
        {
            region: ComparisonRegion.RoiA,
            label: roiAName,
            tooltip: intl.formatMessage(
                { id: 'compare.legend.roiA' },
                { name: roiAName }
            ),
            color: roiAColor,
            outline: true,
        },
        {
            region: ComparisonRegion.RoiB,
            label: roiBName,
            tooltip: intl.formatMessage(
                { id: 'compare.legend.roiB' },
                { name: roiBName }
            ),
            color: roiBColor,
            outline: true,
        },
    ];

    return (
        <Box
            display="flex"
            flexWrap="wrap"
            alignItems="center"
            columnGap={0.75}
            rowGap={0.25}
        >
            {chips.map((chip) => (
                <Tooltip
                    key={chip.region}
                    title={chip.tooltip}
                    placement="right"
                >
                    {/* Focusable + labelled so the "only in …" meaning is
                        reachable by keyboard and screen reader, not hover-only */}
                    <Box
                        component="span"
                        display="inline-flex"
                        alignItems="center"
                        gap={0.5}
                        minWidth={0}
                        tabIndex={0}
                        aria-label={chip.tooltip}
                    >
                        <Swatch color={chip.color} outline={chip.outline} />
                        <Typography
                            component="span"
                            fontSize={12}
                            color="text.secondary"
                            noWrap
                            sx={{ maxWidth: 110 }}
                        >
                            {chip.label}
                        </Typography>
                    </Box>
                </Tooltip>
            ))}
        </Box>
    );
}

/** The three contour-comparison metric rows (DSC / HD95 / MSD). */
function MetricsView({ result }: { result: ContourComparisonMetrics }) {
    return (
        <Stack spacing={0.75}>
            {comparisonMetricRows(result).map((row) => (
                <Box
                    key={row.labelId}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={2}
                >
                    <Typography fontSize={13} color="text.secondary">
                        <FormattedMessage id={row.labelId} />
                    </Typography>
                    <CopyField label={row.value} size={14} />
                </Box>
            ))}
        </Stack>
    );
}

/** Icon + short message row, shared by the loading and unavailable states. */
function StatusRow({
    icon,
    messageId,
    color = 'text.secondary',
}: {
    icon: ReactNode;
    messageId: string;
    color?: string;
}) {
    return (
        <Box display="flex" alignItems="center" gap={1} color={color}>
            {icon}
            <Typography fontSize={12}>
                <FormattedMessage id={messageId} />
            </Typography>
        </Box>
    );
}

/**
 * Metric area for a chosen ROI pair, with an explicit evaluation order
 * (not shown → loading → unavailable → result → nothing yet) rather than a
 * nested ternary. "Not shown" leads: while the set sits on no view there is
 * nothing to compute from, whatever the last compute left behind.
 */
function MetricsSection({
    shown,
    loading,
    error,
    data,
}: {
    shown: boolean;
    loading: boolean;
    error: boolean;
    data: ContourComparisonMetrics | undefined;
}) {
    if (!shown) {
        return (
            <StatusRow
                icon={<Info size={14} />}
                messageId={STRUCTURE_SET_NOT_SHOWN_HINT_ID}
            />
        );
    }
    if (loading) {
        return (
            <StatusRow
                icon={<CircularProgress size={14} color="inherit" />}
                messageId="loading"
            />
        );
    }
    // An empty/point ROI (or any compute failure) surfaces as unavailable.
    if (error) {
        return (
            <StatusRow
                icon={<TriangleAlert size={14} />}
                messageId="compare.error"
                color="warning.main"
            />
        );
    }
    if (data !== undefined) {
        return <MetricsView result={data} />;
    }
    return null;
}

interface ComparePanelContentProps {
    open: boolean;
    anchorRef: RefObject<HTMLElement | null>;
    onClose: () => void;
    shown: boolean;
    loading: boolean;
    data: ContourComparisonMetrics | undefined;
    error: boolean;
}

/** The panel, beside `anchorRef` until the user moves it. */
function ComparePanelContent({
    open,
    anchorRef,
    onClose,
    shown,
    loading,
    data,
    error,
}: ComparePanelContentProps) {
    const intl = useIntl();
    const structureSet = useRecoilValue(currentStructureSetState);
    const roiA = useRecoilValue(comparisonRoiAState);
    const roiB = useRecoilValue(comparisonRoiBState);

    const bothSelected = comparablePair(roiA, roiB) !== null;
    const roiName = (roiNumber: number | null): string =>
        structureSet?.roiArray.find((roi) => roi.roiNumber === roiNumber)
            ?.name ?? '';
    const roiColor = (roiNumber: number | null): string => {
        const roi = structureSet?.roiArray.find(
            (r) => r.roiNumber === roiNumber
        );
        return roi ? roiCss(roi) : 'transparent';
    };

    return (
        <DraggablePanel
            open={open}
            title={intl.formatMessage({ id: 'compare.title' })}
            width={PANEL_WIDTH}
            anchorRef={anchorRef}
            onClose={onClose}
        >
            <Stack spacing={1.5}>
                <RoiPicker />

                {!bothSelected ? (
                    <Typography fontSize={12} color="text.secondary">
                        <FormattedMessage id="compare.selectTwoRois" />
                    </Typography>
                ) : (
                    <Stack spacing={1.5}>
                        <Legend
                            roiAName={roiName(roiA)}
                            roiBName={roiName(roiB)}
                            roiAColor={roiColor(roiA)}
                            roiBColor={roiColor(roiB)}
                        />

                        <Divider />

                        <MetricsSection
                            shown={shown}
                            loading={loading}
                            error={error}
                            data={data}
                        />
                    </Stack>
                )}
            </Stack>
        </DraggablePanel>
    );
}

/**
 * In-viewport contour comparison panel: a {@link DraggablePanel}. Pick two
 * ROIs of the current structure set to see the colour-coded
 * difference-overlay legend and the DICE / HD95 / MSD metrics. The overlay
 * itself is driven by `useContourComparison`; this panel owns the metric
 * lifecycle. While the set sits on no view the panel stays open and says
 * the set is not shown, and the metrics return with the view.
 */
export default function ComparePanel({
    toggleRef,
}: {
    /** The button that toggles the panel, which it opens beside. */
    toggleRef: RefObject<HTMLElement | null>;
}) {
    const [compareModeActive, setCompareModeActive] = useRecoilState(
        compareModeActiveState
    );
    const roiA = useRecoilValue(comparisonRoiAState);
    const roiB = useRecoilValue(comparisonRoiBState);
    const resetRoiA = useResetRecoilState(comparisonRoiAState);
    const resetRoiB = useResetRecoilState(comparisonRoiBState);
    // Re-run metrics automatically when a compared ROI's contour is edited. The
    // live StructureSet reference is stable across edits, so the submit effect
    // below can't observe contour changes on its own; this token bumps on every
    // RoiContoursModifiedEvent for the pair (brush, interpolation, boolean,
    // smoothing, …) and feeds that effect's dependency list.
    const editToken = useComparisonEditToken(roiA, roiB, compareModeActive);
    const {
        submitPair,
        cancelPending,
        loading,
        data,
        error,
        structureSetShown,
    } = useContourComparisonMetrics();

    // On close, clear the ROI pair so a reopen can't re-run the previous
    // session.
    const wasActiveRef = useRef(false);
    useEffect(() => {
        if (!compareModeActive && wasActiveRef.current) {
            // The atoms otherwise persist, since every close path
            // (handleClose, the toolbar toggle, a workflow step change) only
            // flips compareModeActive. A metric compute scheduled for the
            // closed session is dropped — its readback would be wasted work
            // for a hidden panel.
            cancelPending();
            resetRoiA();
            resetRoiB();
        }
        wasActiveRef.current = compareModeActive;
    }, [compareModeActive, cancelPending, resetRoiA, resetRoiB]);

    // Recompute metrics whenever a valid, distinct pair is chosen while compare
    // mode is open. Gated on compareModeActive because this panel stays mounted
    // (it only renders null when closed); without the guard, ROI or
    // structure-set changes would trigger needless readbacks after the user has
    // exited compare mode. submitPair changes identity with the Environment
    // the masks are read from, so a set that leaves every view and returns
    // recomputes on the Environment that shows it.
    useEffect(() => {
        if (!compareModeActive) {
            return;
        }
        const pair = comparablePair(roiA, roiB);
        if (pair) {
            submitPair(pair[0], pair[1]);
        }
    }, [compareModeActive, roiA, roiB, editToken, submitPair]);

    const handleClose = useCallback(
        () => setCompareModeActive(false),
        [setCompareModeActive]
    );

    return (
        <ComparePanelContent
            open={compareModeActive}
            anchorRef={toggleRef}
            onClose={handleClose}
            shown={structureSetShown}
            loading={loading}
            data={data}
            error={error}
        />
    );
}
